
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/format';

interface DashboardCardProps {
  title: string;
  description?: string;
  value: string | number | React.ReactNode;
  change?: {
    value: number;
    type: 'positive' | 'negative' | 'neutral';
    label?: string;
  };
  icon?: React.ReactNode;
  className?: string;
  animated?: boolean;
  children?: React.ReactNode;
  /** F16.1: se presente, la card diventa cliccabile (drill-down). */
  onClick?: () => void;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  description,
  value,
  change,
  icon,
  className,
  animated = true,
  children,
  onClick,
}) => {
  const changeColor = change?.type === 'positive' ? 'text-green-600' :
                    change?.type === 'negative' ? 'text-red-600' :
                    'text-gray-600';

  const interactive = !!onClick;

  return (
    <Card
      className={cn(
        "hover:shadow-lg transition-all duration-300",
        animated && "animate-fade-in hover:scale-105",
        interactive && "cursor-pointer hover:ring-2 hover:ring-primary/30",
        className
      )}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon && (
          <div className="h-4 w-4 text-muted-foreground">
            {icon}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold mb-1">
          {typeof value === 'number' ? value.toLocaleString('it-IT') : value}
        </div>
        {description && (
          <CardDescription className="mb-2">{description}</CardDescription>
        )}
        {change && (
          <p className={cn("text-xs", changeColor)}>
            {change.value > 0 ? '+' : ''}{formatNumber(change.value, 1)}%
            {change.label && ` ${change.label}`}
          </p>
        )}
        {children}
      </CardContent>
    </Card>
  );
};
